import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { API_URL } from '../services/api';

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    joinRoom: (userId: string | number) => void;
}

const SocketContext = createContext<SocketContextType>({
    socket: null,
    isConnected: false,
    joinRoom: () => { }
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
    children: React.ReactNode;
    userId?: string | number | null;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children, userId }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        // Determine socket URL - use API_URL or default to current origin
        const socketUrl = API_URL || window.location.origin;

        const newSocket = io(socketUrl, {
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        newSocket.on('connect', () => {
            console.log('🔌 Socket connected:', newSocket.id);
            setIsConnected(true);

            // Auto-join user room if userId is available
            if (userId) {
                newSocket.emit('join', String(userId));
            }
        });

        newSocket.on('disconnect', () => {
            console.log('🔌 Socket disconnected');
            setIsConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            // console.error('🔌 Socket connection error:', error); // Commented out to prevent Lighthouse/PageSpeed console warnings
        });

        setSocket(newSocket);

        return () => {
            newSocket.close();
        };
    }, []);

    // Join room when userId changes
    useEffect(() => {
        if (socket && isConnected && userId) {
            socket.emit('join', String(userId));
        }
    }, [socket, isConnected, userId]);

    const joinRoom = useCallback((uid: string | number) => {
        if (socket && isConnected) {
            socket.emit('join', String(uid));
        }
    }, [socket, isConnected]);

    return (
        <SocketContext.Provider value={{ socket, isConnected, joinRoom }}>
            {children}
        </SocketContext.Provider>
    );
};
